export const mapWithConcurrency = async <T, R>(
  items: readonly T[],
  concurrency: number,
  mapper: (item: T, index: number) => Promise<R>
): Promise<R[]> => {
  const limit = Math.max(1, Math.min(concurrency, items.length));
  const results = new Array<R>(items.length);
  let nextIndex = 0;

  await Promise.all(
    Array.from({ length: limit }, async () => {
      while (nextIndex < items.length) {
        const index = nextIndex;
        nextIndex += 1;
        results[index] = await mapper(items[index], index);
      }
    })
  );

  return results;
};

export type TopologySitesResolveResult<R> = {
  payloads: Awaited<R>[];
  failedCount: number;
  totalCount: number;
};

export const topologyResolveAllFailed = <R>(
  result: TopologySitesResolveResult<R> | null | undefined
): boolean => Boolean(result && result.totalCount > 0 && result.failedCount === result.totalCount);

export const topologyResolveHasFailures = <R>(
  result: TopologySitesResolveResult<R> | null | undefined
): boolean => Boolean(result && result.totalCount > 0 && result.failedCount > 0);

export const shouldShowProtocolTopologyUnavailable = <R>(
  result: TopologySitesResolveResult<R> | null | undefined,
  rowCount: number
): boolean => topologyResolveHasFailures(result) && rowCount === 0;

export const topologyResolveFailureResult = <R>(totalCount = 1): TopologySitesResolveResult<R> => ({
  payloads: [],
  failedCount: totalCount,
  totalCount
});

export const flattenNestedTopologyResolveResults = <R>(
  result: TopologySitesResolveResult<TopologySitesResolveResult<R>>
): TopologySitesResolveResult<R> => {
  const nestedFailedCount = result.payloads.reduce((total, nestedResult) => total + nestedResult.failedCount, 0);
  const nestedTotalCount = result.payloads.reduce((total, nestedResult) => total + nestedResult.totalCount, 0);

  return {
    payloads: result.payloads.flatMap((nestedResult) => nestedResult.payloads),
    failedCount: result.failedCount + nestedFailedCount,
    totalCount: result.totalCount + nestedTotalCount
  };
};

export const normalizeTopologyProtocols = <P extends string>(
  values: readonly (string | null | undefined)[],
  validProtocols: ReadonlySet<P>
): P[] => {
  const deduped = new Set<P>();

  for (const value of values) {
    const normalizedValue = value?.trim().toLowerCase() ?? '';
    if (validProtocols.has(normalizedValue as P)) {
      deduped.add(normalizedValue as P);
    }
  }

  return [...deduped];
};

export const resolveTopologyForSites = async <T, R>(
  sites: readonly T[],
  concurrency: number,
  resolveSite: (site: T, index: number) => Promise<R>
): Promise<TopologySitesResolveResult<R>> => {
  const results = await mapWithConcurrency(sites, concurrency, async (site, index) => {
    try {
      return { payload: await resolveSite(site, index), failed: false };
    } catch {
      return { payload: null, failed: true };
    }
  });
  return {
    payloads: results
      .map((result) => result.payload)
      .filter((payload): payload is NonNullable<typeof payload> => Boolean(payload)),
    failedCount: results.filter((result) => result.failed).length,
    totalCount: sites.length
  };
};

export const resolveTopologyForProtocols = async <P, R>(
  protocols: readonly P[],
  resolveProtocol: (protocol: P, index: number) => Promise<R>
): Promise<TopologySitesResolveResult<R>> => {
  const results = await Promise.all(
    protocols.map(async (protocol, index) => {
      try {
        return { payload: await resolveProtocol(protocol, index), failed: false };
      } catch {
        return { payload: null, failed: true };
      }
    })
  );

  return {
    payloads: results
      .map((result) => result.payload)
      .filter((payload): payload is NonNullable<typeof payload> => Boolean(payload)),
    failedCount: results.filter((result) => result.failed).length,
    totalCount: protocols.length
  };
};
